/*
 * Google Enhanced Ecommerce Object
 *
 * Basket functionality specific to account change journey.
 * https://confluence.int.plus.net/display/DIGITAL/Google+Enhanced+Ecommerce
 */
const accountChangeBasket = {

    /*
     * Initialise basket state and setup basket event listeners
     *
     * @returns {void}
     */
    initialise: () => {

        if (
            gaEcommerce.accountChangeBasket.isInitialised
            || !gaEcommerce.core.checkStorageCapabilities()
        ) return;

        const basket = gaEcommerce.accountChangeBasket;

        // Load basket contents (persisted in session storage to pass between different pages)
        let cachedProducts = sessionStorage.getItem('ecomProducts');

        if (cachedProducts !== null) {

            try {
                cachedProducts = JSON.parse(cachedProducts);

                const propertiesList = [
                    'list',
                    'position',
                    'timeUntilPromoExpires',
                    'quantity',
                    'interaction',
                    'dimension48'
                ];
                basket.sanitisedProducts = cachedProducts;

                // Strip dynamic product properties
                basket.rawProducts = cachedProducts.map(JSON.parse).map((product) => {

                    propertiesList.map((property) => {
                        if (typeof product[property] !== 'undefined') {
                            delete product[property];
                        }
                    });

                    return product;

                }).map(JSON.stringify);

            } catch (e) {
                console.warn('[gaEcommerce.js] Failed to initialise account change basket');
                basket.rawProducts = [];
                basket.sanitisedProducts = [];
            }

        } else {
            basket.rawProducts = [];
            basket.sanitisedProducts = [];
        }

        // Attach event handlers to inputs and buttons
        gaEcommerce.events.registerAccountChangeBasketEventHandlers();

        gaEcommerce.accountChangeBasket.isInitialised = true;
    },


    /*
     * Handle basket button clicks
     *
     * @param {object} button DOM element
     * @returns {void}
     */
    handleButton: (button) => {
        const ecomProductElem = button.closest('[data-ecom-product]'),
              ecomRulesElem = button.closest('[data-ecom-rules]');

        switch (true) {
            case (button.dataset.ecomBasketAction === 'keep' && ecomRulesElem !== null):
                gaEcommerce.accountChangeBasket.keep(ecomRulesElem);
                break;

            case (ecomProductElem !== null):
                gaEcommerce.accountChangeBasket[
                    button.dataset.ecomBasketAction
                ](ecomProductElem, 'interaction', true);
                break;
        }
    },

    /**
     * Handles radio changes
     *
     * @param {object} el DOM element representing the selected radio button
     * @returns {Function}
     */
    handleBasketRadioChange: (selectedRadioButton) => {
        gaEcommerce.accountChangeBasket.handleRadioButtons(
            selectedRadioButton,
            // Find all other radio buttons with same name
            document.querySelectorAll(
                '[type="radio"]' + '[name="'+ selectedRadioButton.getAttribute('name') + '"]'
            )
        );
    },

    /*
     * Handle changes to radio buttons
     * @todo could/should this be covered by data-ecom-rules?
     *
     * @param {object} selectedRadioButton DOM element
     * @param {NodeList} radioButtons list of button elements
     *
     * @returns {void}
     */
    handleRadioButtons: (selectedRadioButton, radioButtons) => {

        // What did the user ask us to do? Add or remove a product?
        // Note: not a child of data-ecom-product = remove (e.g. 'No Voicemail') button
        const userAction = selectedRadioButton.closest('[data-ecom-product]')
                ? 'add'
                : 'remove';

        radioButtons.forEach((item) => {
            const ecomProductElem = item.closest('[data-ecom-product]');

            if (ecomProductElem) {
                const action = item.checked ? 'add' : 'remove';

                // Tell the basket to TRY to add/remove item depending on whether it's selected
                // Add: only gets added if item NOT in basket
                // Remove: only gets removed if item IS in basket
                if (action === 'add') {
                    gaEcommerce.accountChangeBasket.add(ecomProductElem, 'interaction', true);
                } else if (action  === 'remove') {
                    // Did the user ask to remove an item or is the script removing it to change the variant?
                    var interaction = (userAction === 'remove') ? 'interaction' : 'non-interaction';

                    gaEcommerce.accountChangeBasket.remove(ecomProductElem, interaction, true);
                }
            }
        });
    },

    /*
     * Process lists of products to add and remove
     *
     * @param {array} itemsToAdd
     * @param {array} itemsToRemove
     * @param {string} interaction
     * @param {boolean} processRules
     *
     * @returns {void}
     */
    processItems: (itemsToAdd, itemsToRemove, interaction, processRules) => {

        if (itemsToRemove.length) {
            gaEcommerce.accountChangeBasket.remove(itemsToRemove, interaction, processRules);
        }

        if (itemsToAdd.length) {
            gaEcommerce.accountChangeBasket.add(itemsToAdd, interaction, processRules);
        }
    },

    /*
     * Add product elements to the basket
     *
     * @param {object|array|NodeList} productElements A single product element or list of product elements
     * @param {string} interaction Whether or not this was a user interaction
     * @param {boolean} shouldProcessRules whether or not to process rules
     *
     * @returns {null|boolean}
     */
    add: (productElements, interaction, shouldProcessRules = true) => {
        // If only one product element given wrap it in an array so the loop still handles it
        const productElementsIterable =
            (productElements instanceof Array || productElements instanceof NodeList)
                ? productElements
                : [productElements];

        const newProducts = [], // To be sent to GA
              interactionBoolean = (interaction !== 'non-interaction');

        // Loop through all products and sanitise each one
        productElementsIterable.forEach((productElementItem) => {
            const rawProduct = productElementItem.hasOwnProperty('dataOverride')
                ? productElementItem.dataOverride
                : gaEcommerce.util.parseJsonObj(productElementItem.dataset?.ecomProduct);

            const rawProductString = JSON.stringify(rawProduct);

            if (gaEcommerce.accountChangeBasket.rawProducts.indexOf(rawProductString) === -1) {
                let sanitisedProduct = gaEcommerce.core.validateProduct(rawProduct);

                if (sanitisedProduct !== false) {

                    if (shouldProcessRules) {
                        gaEcommerce.rules.processRules(productElementItem, 'add');
                    }

                    // Add RAW product to the ecommerce basket array
                    // Stored as string so we can check what products are in the array easily
                    gaEcommerce.accountChangeBasket.rawProducts.push(rawProductString);

                    // Add SANITISED product to array of products to send to GA
                    sanitisedProduct = gaEcommerce.core.includeDynamicProductProperties(
                        productElementItem, sanitisedProduct
                    );
                    sanitisedProduct.interaction = interactionBoolean;

                    gaEcommerce.accountChangeBasket.sanitisedProducts.push(JSON.stringify(sanitisedProduct));
                    newProducts.push(sanitisedProduct);
                }
            }
        });

        // Send the fully formed array of products to GA
        if (newProducts.length > 0) {
            gaEcommerce.accountChangeBasket.saveBasket();
        }

        gaEcommerce.core.sendAddData(newProducts);
    },

    /*
     * Remove product elements from the basket
     *
     * @param {object|array} productElements A single product element or list of product elements
     * @param {string} interaction Whether or not the interaction was a user interaction
     * @param {boolean} processRules whether or not to process rules
     *
     * @returns {null|boolean}
     */
    remove: (productElements, interaction, shouldProcessRules = true) => {
        // If only one product element given wrap it in an array so the loop still handles it
        const productElementsIterable =
            (productElements instanceof Array || productElements instanceof NodeList)
                ? productElements
                : [productElements];
        
        const removedProducts = [], // To be sent to GA
              interactionBoolean = (interaction !== 'non-interaction');

        // Loop through all products and sanitise each one
        productElementsIterable.forEach((productElementItem) => {

            const rawProduct = productElementItem.hasOwnProperty('dataOverride')
                ? productElementItem.dataOverride
                : gaEcommerce.util.parseJsonObj(productElementItem.dataset?.ecomProduct);

            const rawProductString = JSON.stringify(rawProduct),
                  productIndex = gaEcommerce.accountChangeBasket.rawProducts.indexOf(rawProductString);

            // Check it's actually in the basket
            if (productIndex !== -1) {

                let sanitisedProduct = gaEcommerce.core.validateProduct(rawProduct);

                if (sanitisedProduct !== false) {

                    // Remove RAW product from the ecommerce basket array
                    gaEcommerce.accountChangeBasket.rawProducts.splice(productIndex, 1);

                    // Remove SANITISED product from the ecommerce basket array
                    gaEcommerce.accountChangeBasket.sanitisedProducts.splice(productIndex, 1);

                    if (shouldProcessRules) {
                        gaEcommerce.rules.processRules(productElementItem, 'remove');
                    }

                    // Add SANITISED product to array of products to send to GA
                    sanitisedProduct = gaEcommerce.core.includeDynamicProductProperties(
                        productElementItem, sanitisedProduct
                    );
                    sanitisedProduct.interaction = interactionBoolean;
                    removedProducts.push(sanitisedProduct);
                }
            }
        });

        if (removedProducts.length > 0) {
            gaEcommerce.accountChangeBasket.saveBasket();
        }

        gaEcommerce.core.sendRemoveData(removedProducts);
    },

    /*
     * Processes any side effects as a result of keeeping an existing product
     *
     * @param {object} rulesElement A single rules element
     *
     * @returns {bolean|void}
     */
    keep: rulesElement => (!!rulesElement && gaEcommerce.rules.processRules(rulesElement, 'keep')),

    /*
     * Returns the products in the GA Basket
     *
     * @returns {Array}
     */
    getBasketContents: () =>
        gaEcommerce.accountChangeBasket.rawProducts
    ,

    /*
     * Add product elements to sessionStorage
     *
     * @returns {void}
     */
    saveBasket: () => {
        clientData.storage.setData(
            {
                name: 'ecomProducts',
                value: JSON.stringify(gaEcommerce.accountChangeBasket.sanitisedProducts)
            },
            'sessionStorage'
        );
    },

    /*
     * Remove all contents from basket
     *
     * @returns {void}
     */
    resetBasket: () => {
        sessionStorage.removeItem('ecomProducts');
        gaEcommerce.accountChangeBasket.rawProducts = [];
        gaEcommerce.accountChangeBasket.sanitisedProducts = [];
    }
};

export default accountChangeBasket;